import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ImageCropModal = ({
  imageSrcInput,
  show,
  onClose,
  onCropSave,
  targetWidth = 64,
  targetHeight = 64,
  initialImageName = 'cropped-image.png',
  imageType = 'image/png'
}) => {
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);

  // Réinitialiser le crop lorsque l'image source change ou que la modale s'ouvre
  useEffect(() => {
    if (imageSrcInput && show) {
      setCrop(undefined); // Important pour que onImageLoad soit bien appelé
      setCompletedCrop(null);
    }
  }, [imageSrcInput, show]);

  const onImageLoad = useCallback((e) => {
    if (!show) return; // Ne pas exécuter si la modale n'est pas visible
    const { naturalWidth: width, naturalHeight: height } = e.currentTarget;
    const aspectRatio = targetWidth / targetHeight;
    const newCrop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90, // Commence avec une sélection de 90% de la largeur
        },
        aspectRatio,
        width,
        height
      ),
      width,
      height
    );
    setCrop(newCrop);
  }, [targetWidth, targetHeight, show]);

  const handleInternalCropSave = async () => {
    if (completedCrop && previewCanvasRef.current && imgRef.current) {
      const canvas = previewCanvasRef.current;
      const image = imgRef.current;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.error('Failed to get 2d context');
        onClose(); // Fermer en cas d'erreur
        return;
      }

      // Créer un canvas temporaire pour le recadrage initial à la résolution naturelle
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = completedCrop.width * scaleX;
      tempCanvas.height = completedCrop.height * scaleY;
      const tempCtx = tempCanvas.getContext('2d');

      if (!tempCtx) {
        console.error('Failed to get temporary 2d context');
        onClose();
        return;
      }
      
      tempCtx.imageSmoothingQuality = 'high';
      tempCtx.drawImage(
        image,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        tempCanvas.width,
        tempCanvas.height
      );

      // Redimensionner l'image recadrée vers targetWidth x targetHeight sur le canvas principal
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(tempCanvas, 0, 0, tempCanvas.width, tempCanvas.height, 0, 0, targetWidth, targetHeight);
      
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error('Canvas is empty after cropping.');
            onClose();
            return;
          }
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => {
            onCropSave(reader.result, initialImageName, imageType); // Envoyer le dataURL, nom, type
          };
        },
        imageType, // Utiliser le type de l'image originale
        0.92 // Qualité pour JPEG/WEBP, 1 pour PNG est souvent ignoré ou par défaut
      );
    }
    onClose(); // Fermer la modale après avoir sauvegardé (ou tenté de sauvegarder)
  };

  const handleInternalClose = () => {
    onClose();
  };

  if (!show || !imageSrcInput) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 transition-opacity duration-300 ease-in-out"
      onClick={(e) => { if (e.target === e.currentTarget) handleInternalClose(); }}
    >
      <div className="bg-blanc dark:bg-dark-secondary p-6 rounded-lg shadow-xl max-w-xl w-full transform transition-all duration-300 ease-in-out scale-100">
        <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">Recadrer l'image</h2>
        <p className="text-sm mb-1 text-gray-600 dark:text-gray-300">
          Ajustez le cadre pour obtenir une image de {targetWidth}x{targetHeight} pixels.
        </p>
        <p className="text-xs mb-4 text-gray-500 dark:text-gray-400">
          Pour un meilleur résultat, utilisez une image avec un fond transparent si possible.
        </p>
        <div className="max-h-[60vh] overflow-auto mb-4 bg-gray-100 dark:bg-gray-700 rounded p-2">
          {imageSrcInput && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={targetWidth / targetHeight}
              // minWidth={targetWidth > 50 ? targetWidth : undefined} // Pour éviter des crop trop petits
              // minHeight={targetHeight > 50 ? targetHeight : undefined}
            >
              <img
                ref={imgRef}
                alt="À recadrer"
                src={imageSrcInput}
                onLoad={onImageLoad}
                style={{ maxHeight: '55vh', objectFit: 'contain', display: 'block', margin: 'auto' }}
              />
            </ReactCrop>
          )}
        </div>
        {/* Canvas caché pour le rendu de l'image recadrée */}
        <canvas ref={previewCanvasRef} style={{ display: 'none' }} />
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleInternalClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500 transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleInternalCropSave}
            disabled={!completedCrop?.width || !completedCrop?.height}
            className="px-4 py-2 text-white bg-orange rounded-lg hover:bg-framboise disabled:opacity-50 transition-colors"
          >
            Appliquer et Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal; 